import React, { useMemo } from "react";
import { SwipeableDrawer } from "@mui/material";
import {
  Calendar,
  Users,
  DollarSign,
  Settings,
  CompassIcon,
  Star,
  BriefcaseMedical,
  GitPullRequestDraft,
  Refrigerator,
  PackagePlus,
  PackageCheck,
  CalendarClock,
  Stethoscope,
  BarChart3,
  Mail,
  BellRing,
  FlaskConical,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDoctorUnreadMessages } from "../../hooks/useDoctorUnreadMessages";
import { isAdminEmail } from "../../utils/adminAccess";

const DoctorSidebar = ({ activeTab, setActiveTab, onItemClick }) => {
  const navigate = useNavigate();

  const doctor = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("doctor") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isAdmin = isAdminEmail(doctor?.email);
  const { unreadMessages } = useDoctorUnreadMessages(doctor?._id);

  const baseItems = [
    {
      label: "Dashboard",
      icon: Calendar,
      path: "/dashboard/home",
    },
    {
      label: "Patients",
      icon: Users,
      path: "/dashboard/patients",
    },
    {
      label: "Availability",
      icon: CalendarClock,
      path: "/dashboard/availability",
    },
    {
      label: "Reminders",
      icon: BellRing,
      path: "/dashboard/reminders",
    },
    {
      label: "Earnings",
      icon: DollarSign,
      path: "/dashboard/earning",
    },
    {
      label: "Settings",
      icon: Settings,
      path: "/dashboard/settings",
    },
  ];

  const adminItems = [
    {
      label: "Pebbles",
      icon: FlaskConical,
      path: "/admin/pebbles",
    },
    {
      label: "Admin Dashboard",
      icon: BarChart3,
      path: "/admin/dashboard",
    },
    {
      label: "Admin",
      icon: CompassIcon,
      path: "/admin/patients",
    },
    {
      label: "Doctors",
      icon: Stethoscope,
      path: "/admin/doctors",
    },
    {
      label: "Admin Reminders",
      icon: Mail,
      path: "/admin/reminders",
    },
    {
      label: "Reviews",
      icon: Star,
      path: "/admin/reviews",
    },
    {
      label: "Pharmacy",
      icon: BriefcaseMedical,
      path: "/admin/pharmacies",
    },
    {
      label: "Home Tests",
      icon: PackageCheck,
      path: "/admin/home-tests",
    },
    {
      label: "Requests",
      icon: GitPullRequestDraft,
      path: "/admin/requests",
    },
    {
      label: "Referrals",
      icon: Refrigerator,
      path: "/admin/referrals",
    },
    {
      label: "Client Content",
      icon: PackagePlus,
      path: "/admin/client-content",
    },
  ];

  const sidebarItems = isAdmin ? [...baseItems, ...adminItems] : baseItems;

  const handleNavigate = (item) => {
    setActiveTab?.(item.label);
    navigate(item.path);
    onItemClick?.();
  };

  const initials =
    doctor?.name
      ?.split(" ")
      ?.filter(Boolean)
      ?.map((item) => item[0])
      ?.join("")
      ?.slice(0, 2)
      ?.toUpperCase() || "DR";

  return (
    <aside className="flex h-full w-72 flex-shrink-0 flex-col border-r border-slate-200 bg-white">
      {/* Brand */}
     

      {/* Profile */}
      <div className="border-b border-slate-100 px-5 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
            {initials}
          </div>

          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-950">
              {doctor?.name || "Doctor"}
            </p>
            <p className="mt-0.5 truncate text-xs text-slate-400">
              {doctor?.email || "doctor account"}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <div className="space-y-1">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.label;

            return (
              <button
                key={item.label}
                type="button"
                onClick={() => handleNavigate(item)}
                className={`flex h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition ${
                  active
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                }`}
              >
                <Icon
                  size={17}
                  className={active ? "text-white" : "text-slate-400"}
                />

                <span className="min-w-0 flex-1 truncate">{item.label}</span>

                {item.label === "Patients" && unreadMessages > 0 && (
                  <span
                    className={`inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                      active
                        ? "bg-white text-slate-900"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {unreadMessages > 99 ? "99+" : unreadMessages}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer note */}
      
    </aside>
  );
};

export const DoctorSidebarDrawer = ({
  open,
  onOpen,
  onClose,
  activeTab,
  setActiveTab,
}) => (
  <SwipeableDrawer
    anchor="left"
    open={open}
    onOpen={onOpen}
    onClose={onClose}
    disableSwipeToOpen={false}
    swipeAreaWidth={36}
    ModalProps={{ keepMounted: true }}
    PaperProps={{
      sx: {
        width: 280,
      },
    }}
  >
    <div className="h-full">
      <DoctorSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onItemClick={onClose}
      />
    </div>
  </SwipeableDrawer>
);

export default DoctorSidebar;
